import axios from 'axios';
import type { Dispatch } from 'redux';
import type { RootState } from '@/reducers';
import type {
	Pipeline,
	PipelineItem,
	PipelineColumn,
} from '@/reducers/pipelineReducer';

// Action types
export const PIPELINE_ACTIONS = {
	SET_PIPELINES: 'SET_PIPELINES',
	SET_CURRENT_PIPELINE: 'SET_CURRENT_PIPELINE',
	SET_PIPELINE_ITEMS: 'SET_PIPELINE_ITEMS',
	SET_LOADING: 'SET_LOADING',
	SET_ERROR: 'SET_ERROR',
	ADD_PIPELINE: 'ADD_PIPELINE',
	UPDATE_PIPELINE: 'UPDATE_PIPELINE',
	DELETE_PIPELINE: 'DELETE_PIPELINE',
	ADD_ITEM: 'ADD_ITEM',
	UPDATE_ITEM: 'UPDATE_ITEM',
	UPDATE_ITEM_CUSTOM_FIELD: 'UPDATE_ITEM_CUSTOM_FIELD',
	MOVE_ITEM: 'MOVE_ITEM',
	DELETE_ITEM: 'DELETE_ITEM',
	SET_SELECTED_ITEMS: 'SET_SELECTED_ITEMS',
	SET_DRAGGED_ITEM: 'SET_DRAGGED_ITEM',
	SET_PIPELINE_SUMMARY: 'SET_PIPELINE_SUMMARY',
	SET_CONVERSATION_STATES: 'SET_CONVERSATION_STATES',
	UPDATE_CONVERSATION_STATE: 'UPDATE_CONVERSATION_STATE',
	MARK_PIPELINE_ITEMS_SEEN: 'MARK_PIPELINE_ITEMS_SEEN',
	CLEAR_PIPELINE_STATE: 'CLEAR_PIPELINE_STATE',
} as const;

// ===========================
// Pipeline CRUD Operations
// ===========================

// Fetch all pipelines
export const fetchPipelines = () => async (dispatch: Dispatch) => {
	try {
		dispatch({ type: PIPELINE_ACTIONS.SET_LOADING, payload: true });

		const res = await axios.post('/api/pipeline/list');

		if (res.data.pipelines && !res.data.error) {
			dispatch({
				type: PIPELINE_ACTIONS.SET_PIPELINES,
				payload: res.data.pipelines,
			});
		} else {
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: res.data.error || 'Failed to fetch pipelines',
			});
		}
	} catch (error: any) {
		console.error('Error fetching pipelines:', error);
		dispatch({
			type: PIPELINE_ACTIONS.SET_ERROR,
			payload: error.response?.data?.error || 'Failed to fetch pipelines',
		});
	} finally {
		dispatch({ type: PIPELINE_ACTIONS.SET_LOADING, payload: false });
	}
};

// Fetch a specific pipeline with its items
export const fetchPipeline =
	(_pipeline: string) => async (dispatch: Dispatch) => {
		try {
			dispatch({ type: PIPELINE_ACTIONS.SET_LOADING, payload: true });

			const res = await axios.get(`/api/pipeline/${_pipeline}`);

			if (res.data.pipeline && res.data.items) {
				dispatch({
					type: PIPELINE_ACTIONS.SET_CURRENT_PIPELINE,
					payload: res.data.pipeline,
				});
				dispatch({
					type: PIPELINE_ACTIONS.SET_PIPELINE_ITEMS,
					payload: {
						pipelineId: _pipeline,
						items: res.data.items,
					},
				});
				dispatch({
					type: PIPELINE_ACTIONS.SET_CONVERSATION_STATES,
					payload: res.data.conversations || {},
				});
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to fetch pipeline',
				});
			}
		} catch (error: any) {
			console.error('Error fetching pipeline:', error);
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload:
					error.response?.data?.error || 'Failed to fetch pipeline',
			});
		} finally {
			dispatch({ type: PIPELINE_ACTIONS.SET_LOADING, payload: false });
		}
	};

// Create a new pipeline
export const createPipeline =
	(data: {
		name: string;
		description?: string;
		columns?: Omit<PipelineColumn, 'position'>[];
	}) =>
	async (dispatch: Dispatch) => {
		try {
			dispatch({ type: PIPELINE_ACTIONS.SET_LOADING, payload: true });

			const res = await axios.post('/api/pipeline/create', data);

			if (res.data.pipeline && !res.data.error) {
				dispatch({
					type: PIPELINE_ACTIONS.ADD_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true, pipeline: res.data.pipeline };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to create pipeline',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error creating pipeline:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to create pipeline';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		} finally {
			dispatch({ type: PIPELINE_ACTIONS.SET_LOADING, payload: false });
		}
	};

// Update pipeline metadata
export const updatePipeline =
	(_pipeline: string, updates: Partial<Pipeline>) =>
	async (dispatch: Dispatch) => {
		try {
			const res = await axios.put(`/api/pipeline/${_pipeline}`, updates);

			if (res.data.pipeline && !res.data.error) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to update pipeline',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error updating pipeline:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to update pipeline';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Delete (archive) a pipeline
export const deletePipeline =
	(_pipeline: string) => async (dispatch: Dispatch) => {
		try {
			const res = await axios.delete(`/api/pipeline/${_pipeline}`);

			if (res.data.success) {
				dispatch({
					type: PIPELINE_ACTIONS.DELETE_PIPELINE,
					payload: _pipeline,
				});
				return { success: true };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to delete pipeline',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error deleting pipeline:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to delete pipeline';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// ===========================
// Column Management
// ===========================

// Add a column to a pipeline
export const addColumn =
	(
		_pipeline: string,
		columnData: {
			name: string;
			color?: string;
			position?: number;
			itemLimit?: number | null;
		}
	) =>
	async (dispatch: Dispatch) => {
		try {
			const res = await axios.post(
				`/api/pipeline/${_pipeline}/column`,
				columnData
			);

			if (res.data.pipeline && !res.data.error) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to add column',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error adding column:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to add column';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Update a column
export const updateColumn =
	(
		_pipeline: string,
		columnId: string,
		updates: {
			name?: string;
			color?: string;
			itemLimit?: number | null;
			onEnterSms?: { enabled: boolean; message: string };
		}
	) =>
	async (dispatch: Dispatch) => {
		try {
			const res = await axios.put(
				`/api/pipeline/${_pipeline}/column/${columnId}`,
				updates
			);

			if (res.data.pipeline && !res.data.error) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to update column',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error updating column:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to update column';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Reorder columns
export const reorderColumns =
	(_pipeline: string, columnOrder: string[]) =>
	async (dispatch: Dispatch) => {
		// Optimistic update
		dispatch({
			type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
			payload: { columns: columnOrder },
		});

		try {
			const res = await axios.put(
				`/api/pipeline/${_pipeline}/columns/reorder`,
				{ columnOrder }
			);

			if (res.data.pipeline && !res.data.error) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true };
			} else {
				// Revert on error
				await dispatch(fetchPipeline(_pipeline) as any);
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to reorder columns',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error reordering columns:', error);
			// Revert on error
			await dispatch(fetchPipeline(_pipeline) as any);
			const errorMessage =
				error.response?.data?.error || 'Failed to reorder columns';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Delete a column
export const deleteColumn =
	(_pipeline: string, columnId: string) => async (dispatch: Dispatch) => {
		try {
			const res = await axios.delete(
				`/api/pipeline/${_pipeline}/column/${columnId}`
			);

			if (res.data.pipeline && !res.data.error) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to delete column',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error deleting column:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to delete column';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// ===========================
// Pipeline Item Operations
// ===========================

// Create a new item
export const createItem =
	(
		_pipeline: string,
		itemData: {
			name: string;
			description?: string;
			columnId: string;
			_assignee?: string;
			_customer?: string;
			priority?: 'low' | 'medium' | 'high' | 'urgent';
			dueDate?: Date | string;
			tags?: string[];
			color?: string;
			linkedEntities?: Array<{
				entityType: 'customer' | 'message' | 'review' | 'campaign';
				_entity: string;
			}>;
		}
	) =>
	async (dispatch: Dispatch) => {
		try {
			const res = await axios.post(
				`/api/pipeline/${_pipeline}/item`,
				itemData
			);

			if (res.data.item && !res.data.error) {
				dispatch({
					type: PIPELINE_ACTIONS.ADD_ITEM,
					payload: {
						pipelineId: _pipeline,
						item: res.data.item,
					},
				});
				return { success: true, item: res.data.item };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to create item',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error creating item:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to create item';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Update an item
export const updateItem =
	(_item: string, _pipeline: string, updates: Partial<PipelineItem>) =>
	async (dispatch: Dispatch) => {
		// Optimistic update
		dispatch({
			type: PIPELINE_ACTIONS.UPDATE_ITEM,
			payload: {
				pipelineId: _pipeline,
				item: { _id: _item, ...updates },
			},
		});

		try {
			const res = await axios.put(`/api/pipeline/item/${_item}`, updates);

			if (res.data.item && !res.data.error) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_ITEM,
					payload: {
						pipelineId: _pipeline,
						item: res.data.item,
					},
				});
				return {
					success: true,
					item: res.data.item,
					reviewRequest: res.data.reviewRequest,
				};
			} else {
				// Revert on error
				await dispatch(fetchPipeline(_pipeline) as any);
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to update item',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error updating item:', error);
			// Revert on error
			await dispatch(fetchPipeline(_pipeline) as any);
			const errorMessage =
				error.response?.data?.error || 'Failed to update item';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Move an item (drag and drop)
export const moveItem =
	(
		_item: string,
		_pipeline: string,
		fromColumn: string,
		toColumn: string,
		position: number
	) =>
	async (dispatch: Dispatch) => {
		// Optimistic update
		dispatch({
			type: PIPELINE_ACTIONS.MOVE_ITEM,
			payload: {
				pipelineId: _pipeline,
				itemId: _item,
				fromColumn,
				toColumn,
				position,
			},
		});

		try {
			const res = await axios.put(`/api/pipeline/item/${_item}/move`, {
				columnId: toColumn,
				position,
			});

			if (res.data.item && !res.data.error) {
				// Fetch updated items to ensure consistency
				await dispatch(fetchPipeline(_pipeline) as any);
				return {
					success: true,
					item: res.data.item,
					stageSms: res.data.stageSms,
				};
			} else {
				// Revert on error
				await dispatch(fetchPipeline(_pipeline) as any);
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to move item',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error moving item:', error);
			// Revert on error
			await dispatch(fetchPipeline(_pipeline) as any);
			const errorMessage =
				error.response?.data?.error || 'Failed to move item';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Delete (archive) an item
export const deleteItem =
	(_item: string, _pipeline: string) => async (dispatch: Dispatch) => {
		try {
			const res = await axios.delete(`/api/pipeline/item/${_item}`);

			if (res.data.success) {
				dispatch({
					type: PIPELINE_ACTIONS.DELETE_ITEM,
					payload: {
						pipelineId: _pipeline,
						itemId: _item,
					},
				});
				return { success: true };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to delete item',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error deleting item:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to delete item';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Fetch the lightweight pipeline summary for the current user (sidebar badge).
export const fetchPipelineSummary = () => async (dispatch: Dispatch) => {
	try {
		const res = await axios.get('/api/pipeline/my-summary');

		if (res.data && !res.data.error) {
			dispatch({
				type: PIPELINE_ACTIONS.SET_PIPELINE_SUMMARY,
				payload: {
					assignedActiveCount: res.data.assignedActiveCount || 0,
					hasAssigneeUnseen: !!res.data.hasAssigneeUnseen,
					hasBusinessUnseen: !!res.data.hasBusinessUnseen,
					hasUnseen: !!res.data.hasUnseen,
				},
			});
			return { success: true };
		}
		return { success: false };
	} catch (error: any) {
		console.error('Error fetching pipeline summary:', error);
		return { success: false };
	}
};

// Mark a pipeline item as seen by the current user (clears unread indicator).
export const markItemSeen =
	(_item: string, _pipeline: string) =>
	async (dispatch: Dispatch, getState: () => RootState) => {
		try {
			const state = getState();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const currentUserId = (state as any).auth?.user?._id;

			// Optimistically add the current user to the item's _seenBy list.
			if (currentUserId) {
				const items = state.pipeline.items[_pipeline] || [];
				const existing = items.find((i) => i._id === _item);
				if (existing && !existing._seenBy?.includes(currentUserId)) {
					dispatch({
						type: PIPELINE_ACTIONS.UPDATE_ITEM,
						payload: {
							pipelineId: _pipeline,
							item: {
								_id: _item,
								_seenBy: [
									...(existing._seenBy || []),
									currentUserId,
								],
							},
						},
					});
				}
			}

			const res = await axios.put(`/api/pipeline/item/${_item}/seen`);

			// Refresh the sidebar summary so the unread indicator updates.
			dispatch(fetchPipelineSummary() as any);

			if (res.data?.success) {
				return { success: true };
			}
			return { success: false };
		} catch (error: any) {
			console.error('Error marking item as seen:', error);
			return { success: false };
		}
	};

// Mark every item in a pipeline as seen by the current user. Called when the
// user views the board — seeing the board counts as seeing its items, which
// is what clears the sidebar unseen dot.
export const markPipelineSeen =
	(_pipeline: string) =>
	async (dispatch: Dispatch, getState: () => RootState) => {
		try {
			const state = getState();
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			const currentUserId = (state as any).auth?.user?._id;

			const res = await axios.put(`/api/pipeline/${_pipeline}/items/seen`);

			if (!res.data?.success) {
				return { success: false };
			}

			// Reflect the new seen-state locally so card unread dots clear
			// without a refetch.
			if (currentUserId) {
				dispatch({
					type: PIPELINE_ACTIONS.MARK_PIPELINE_ITEMS_SEEN,
					payload: { pipelineId: _pipeline, userId: currentUserId },
				});
			}

			// Refresh the sidebar summary so the unseen dot updates now
			// instead of on the next poll.
			dispatch(fetchPipelineSummary() as any);

			return { success: true };
		} catch (error: any) {
			console.error('Error marking pipeline as seen:', error);
			return { success: false };
		}
	};

// Resolve a ticket number to its item/pipeline ids (shareable
// /pipeline/item/:itemNumber links). Plain fetch, no dispatch — the caller
// decides what to load next. Throws on not-found.
export const resolvePipelineItemByNumber = async (
	itemNumber: number
): Promise<{
	_id: string;
	name: string;
	status: string;
	_pipeline: string;
	itemNumber: number;
}> => {
	// 404/400 are expected outcomes (dead links); accepting them here keeps
	// the global axios error interceptor from toasting a duplicate error.
	const res = await axios.get(`/api/pipeline/item/by-number/${itemNumber}`, {
		validateStatus: (status) =>
			status === 200 || status === 400 || status === 404,
	});
	if (!res.data?.item) {
		throw new Error(res.data?.error || 'Item not found');
	}
	return res.data.item;
};

// Get items for a specific customer
export const fetchCustomerItems =
	(_customer: string) => async (dispatch: Dispatch) => {
		try {
			const res = await axios.get(
				`/api/pipeline/customer/${_customer}/items`
			);

			if (res.data.items) {
				// We'll return the items directly rather than updating Redux
				// since this is for a specific customer view
				return { success: true, items: res.data.items };
			} else {
				return {
					success: false,
					error: res.data.error || 'Failed to fetch items',
				};
			}
		} catch (error: any) {
			console.error('Error fetching customer items:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to fetch customer items';
			return { success: false, error: errorMessage };
		}
	};

// ===========================
// Bulk Operations
// ===========================

// Bulk move items
export const bulkMoveItems =
	(itemIds: string[], _pipeline: string, columnId: string) =>
	async (dispatch: Dispatch) => {
		try {
			const res = await axios.put('/api/pipeline/items/bulk-move', {
				itemIds,
				columnId,
			});

			if (res.data.items && !res.data.error) {
				// Fetch updated pipeline to ensure consistency
				await dispatch(fetchPipeline(_pipeline) as any);
				return { success: true, items: res.data.items };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to move items',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error bulk moving items:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to move items';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Bulk assign items
export const bulkAssignItems =
	(itemIds: string[], _pipeline: string, _assignee: string | null) =>
	async (dispatch: Dispatch) => {
		try {
			const res = await axios.put('/api/pipeline/items/bulk-assign', {
				itemIds,
				_assignee,
			});

			if (res.data.items && !res.data.error) {
				// Update items in state
				res.data.items.forEach((item: PipelineItem) => {
					dispatch({
						type: PIPELINE_ACTIONS.UPDATE_ITEM,
						payload: {
							pipelineId: _pipeline,
							item,
						},
					});
				});
				return { success: true, items: res.data.items };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to assign items',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error bulk assigning items:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to assign items';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// ===========================
// UI State Management
// ===========================

// Set selected items (for bulk operations)
export const setSelectedItems = (itemIds: string[]) => ({
	type: PIPELINE_ACTIONS.SET_SELECTED_ITEMS,
	payload: itemIds,
});

// Set dragged item (for drag visualization)
export const setDraggedItem = (item: PipelineItem | null) => ({
	type: PIPELINE_ACTIONS.SET_DRAGGED_ITEM,
	payload: item,
});

// Clear all pipeline state
export const clearPipelineState = () => ({
	type: PIPELINE_ACTIONS.CLEAR_PIPELINE_STATE,
});

// Set loading state
export const setLoading = (loading: boolean) => ({
	type: PIPELINE_ACTIONS.SET_LOADING,
	payload: loading,
});

// Set error state
export const setError = (error: string | null) => ({
	type: PIPELINE_ACTIONS.SET_ERROR,
	payload: error,
});

// ===========================
// Property Definition Actions
// ===========================

// Add a property definition to a pipeline
export const addPropertyDefinition =
	(_pipeline: string, propertyData: any) => async (dispatch: Dispatch) => {
		try {
			const res = await axios.post(
				`/api/pipeline/${_pipeline}/property`,
				propertyData
			);

			if (res.data.pipeline) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true, pipeline: res.data.pipeline };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to add property',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error adding property:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to add property';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Update a property definition
export const updatePropertyDefinition =
	(_pipeline: string, propertyId: string, updates: any) =>
	async (dispatch: Dispatch) => {
		try {
			const res = await axios.put(
				`/api/pipeline/${_pipeline}/property/${propertyId}`,
				updates
			);

			if (res.data.pipeline) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to update property',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error updating property:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to update property';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Delete a property definition
export const deletePropertyDefinition =
	(_pipeline: string, propertyId: string) => async (dispatch: Dispatch) => {
		try {
			const res = await axios.delete(
				`/api/pipeline/${_pipeline}/property/${propertyId}`
			);

			if (res.data.pipeline) {
				dispatch({
					type: PIPELINE_ACTIONS.UPDATE_PIPELINE,
					payload: res.data.pipeline,
				});
				return { success: true };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to delete property',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error deleting property:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to delete property';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};

// Update a custom field value for an item
export const updateItemCustomField =
	(_item: string, propertyId: string, value: any) =>
	async (dispatch: Dispatch, getState: any) => {
		try {
			const res = await axios.put(
				`/api/pipeline/item/${_item}/custom-field`,
				{ propertyId, value }
			);

			if (res.data.item) {
				// Find which pipeline this item belongs to
				const state = getState();
				let pipelineId: string | null = null;

				for (const [pid, items] of Object.entries(
					state.pipeline.items
				)) {
					if ((items as any[]).some((item) => item._id === _item)) {
						pipelineId = pid;
						break;
					}
				}

				if (pipelineId) {
					dispatch({
						type: PIPELINE_ACTIONS.UPDATE_ITEM,
						payload: {
							pipelineId,
							item: res.data.item,
						},
					});
				}
				return { success: true, item: res.data.item };
			} else {
				dispatch({
					type: PIPELINE_ACTIONS.SET_ERROR,
					payload: res.data.error || 'Failed to update custom field',
				});
				return { success: false, error: res.data.error };
			}
		} catch (error: any) {
			console.error('Error updating custom field:', error);
			const errorMessage =
				error.response?.data?.error || 'Failed to update custom field';
			dispatch({
				type: PIPELINE_ACTIONS.SET_ERROR,
				payload: errorMessage,
			});
			return { success: false, error: errorMessage };
		}
	};
